import { useState } from "react";
import { AppShell } from "../components/layout/AppShell";
import { DataTable, type Column } from "../components/ui/DataTable";
import { Badge } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { Modal } from "../components/ui/Modal";
import { StepUpDialog } from "../components/ui/StepUpDialog";
import { Input, Select } from "../components/ui/Fields";
import { usePagedList } from "../hooks/usePagedList";
import { apiRequest, ApiError } from "../api/client";
import { useToast } from "../components/ui/Toast";
import { useAuth } from "../auth/AuthContext";
import type { AdminUser } from "../api/types";

type PendingAction =
  | { kind: "grant"; email: string; role: "super_admin" | "moderator" }
  | { kind: "role-change"; admin: AdminUser; newRole: "super_admin" | "moderator" }
  | { kind: "revoke"; admin: AdminUser };

export function AdminUsersPage() {
  const { rows, isLoading, error, page, setPage, hasNextPage, reload } = usePagedList<AdminUser>("/admin/admin-users");
  const { session } = useAuth();
  const [isInviting, setIsInviting] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState<"super_admin" | "moderator">("moderator");
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  async function runStepUpAction(stepUpToken: string) {
    if (!pending) return;
    setIsSubmitting(true);
    try {
      if (pending.kind === "grant") {
        await apiRequest("/admin/admin-users", { method: "POST", body: { email: pending.email, role: pending.role, stepUpToken } });
        showToast(`${pending.email} is now an admin`, "success");
        setNewEmail("");
        setIsInviting(false);
      } else if (pending.kind === "role-change") {
        await apiRequest(`/admin/admin-users/${pending.admin.id}`, { method: "PATCH", body: { role: pending.newRole, stepUpToken } });
        showToast("Role updated", "success");
      } else if (pending.kind === "revoke") {
        await apiRequest(`/admin/admin-users/${pending.admin.id}`, { method: "DELETE", body: { stepUpToken } });
        showToast("Access revoked", "success");
      }
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not complete that action", "error");
    } finally {
      setIsSubmitting(false);
      setPending(null);
    }
  }

  const columns: Column<AdminUser>[] = [
    { header: "Email", render: (row) => <span className="font-medium">{row.email}</span> },
    {
      header: "Role",
      render: (row) =>
        row.email === session?.email ? (
          <Badge tone="gold">{row.role.replace("_", " ")} (you)</Badge>
        ) : (
          <Select
            value={row.role}
            onChange={(e) => setPending({ kind: "role-change", admin: row, newRole: e.target.value as "super_admin" | "moderator" })}
            className="w-auto"
          >
            <option value="moderator">Moderator</option>
            <option value="super_admin">Super Admin</option>
          </Select>
        ),
    },
    { header: "Granted", render: (row) => new Date(row.granted_at).toLocaleDateString() },
    {
      header: "",
      className: "text-right",
      render: (row) =>
        row.email === session?.email ? null : (
          <Button size="sm" variant="danger" onClick={() => setPending({ kind: "revoke", admin: row })}>
            Revoke
          </Button>
        ),
    },
  ];

  return (
    <AppShell title="Admin Users">
      <div className="mb-4 flex justify-between">
        <p className="max-w-lg text-xs text-ink-faint">
          Granting admin access requires the person already have a real account in the app — this never creates one.
          Every action here requires re-entering your password.
        </p>
        <Button onClick={() => setIsInviting(true)}>+ Grant Admin Access</Button>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        keyField={(r) => r.id}
        isLoading={isLoading}
        error={error}
        onRetry={reload}
        emptyTitle="No admins found"
        pagination={{ page, pageSize: 20, hasNextPage, onPageChange: setPage }}
      />

      <Modal
        isOpen={isInviting}
        onClose={() => setIsInviting(false)}
        title="Grant Admin Access"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsInviting(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!newEmail) {
                  showToast("Email is required", "error");
                  return;
                }
                setPending({ kind: "grant", email: newEmail, role: newRole });
              }}
            >
              Continue
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input label="Existing account email" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
          <Select label="Role" value={newRole} onChange={(e) => setNewRole(e.target.value as "super_admin" | "moderator")}>
            <option value="moderator">Moderator</option>
            <option value="super_admin">Super Admin</option>
          </Select>
        </div>
      </Modal>

      <StepUpDialog
        isOpen={!!pending}
        onClose={() => setPending(null)}
        onVerified={runStepUpAction}
        title={
          pending?.kind === "grant"
            ? `Grant ${pending.role.replace("_", " ")} access to ${pending.email}?`
            : pending?.kind === "role-change"
            ? `Change ${pending.admin.email}'s role to ${pending.newRole.replace("_", " ")}?`
            : pending?.kind === "revoke"
            ? `Revoke access for ${pending.admin.email}?`
            : ""
        }
        description="This is one of the most sensitive actions in the panel — re-enter your password to confirm."
      />
    </AppShell>
  );
}
